import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Zap } from 'lucide-react';
import { buildApiUrl } from '../../../services/apiConfig';

const AgentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSkillIndex, setSelectedSkillIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [response, setResponse] = useState(null);
  const [tryLoading, setTryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const res = await fetch(buildApiUrl('AGENTS', ''));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        const agents = await res.json();
        const found = agents.find(a => a.agent_id === id);
        if (found) {
          setAgent(found);
          const firstSkill = found.skills?.[0];
          const initial = {};
          Object.keys(firstSkill?.args_schema?.properties || {}).forEach(key => { initial[key] = ''; });
          setFormData(initial);
        }
      } catch (err) {
        console.error('Error fetching agent:', err);
        setError('Failed to load agent details.');
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTry = async () => {
    setTryLoading(true);
    setResponse(null);
    setError(null);
    try {
      const inputMode = selectedSkill?.input_modes?.[0] ?? 'application/json';
      const properties = selectedSkill?.args_schema?.properties || {};

      let headers = {};
      let body;

      if (inputMode === 'application/json') {
        const jsonBody = {};
        Object.entries(formData).forEach(([k, v]) => {
          if (v !== '') {
            jsonBody[k] = properties[k]?.type === 'array'
              ? v.split(',').map(s => s.trim()).filter(Boolean)
              : v;
          }
        });
        headers = { 'Content-Type': 'application/json' };
        body = JSON.stringify(jsonBody);
      } else if (inputMode === 'multipart/form-data') {
        const form = new FormData();
        Object.entries(formData).forEach(([k, v]) => { if (v !== '') form.append(k, v); });
        // Don't set Content-Type — browser sets it automatically with the boundary
        body = form;
      } else {
        const form = new URLSearchParams();
        Object.entries(formData).forEach(([k, v]) => { if (v !== '') form.append(k, v); });
        headers = { 'Content-Type': inputMode };
        body = form.toString();
      }

      const skillPath = selectedSkill?.path ?? '';
      const url = skillPath
        ? `${agent.base_url.replace(/\/$/, '')}/${skillPath.replace(/^\//, '')}`
        : agent.base_url;

      const res = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });
      const data = await res.json();
      setResponse(data);
    } catch (err) {
      setError(`Request failed: ${err.message}`);
    } finally {
      setTryLoading(false);
    }
  };

  const selectedSkill = agent?.skills?.[selectedSkillIndex] ?? agent?.skills?.[0];
  const required = selectedSkill?.args_schema?.required || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-primary"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Agent not found.</p>
    
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 py-8">

        {/* Agent header — open layout, no card box */}
        <div className="mb-8 pb-8 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
            <button
              onClick={() => navigate(-1)}
              className="text-primary hover:text-primaryDark transition-colors flex-shrink-0"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-3xl font-bold text-primary">{agent.name}</h1>
            {agent.version && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full font-medium">
                v{agent.version}
              </span>
            )}
            {agent.agent_type && (
              <span className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full font-semibold capitalize">
                {agent.agent_type}
              </span>
            )}
          </div>

          <p className="text-sm text-gray-400 mb-3">by {agent.publisher || 'Unknown'}</p>
          <p className="text-gray-600 leading-relaxed max-w-2xl">{agent.description}</p>

          {agent.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {agent.tags.map(tag => (
                <span key={tag} className="text-xs bg-secondary/10 text-secondary px-2.5 py-1 rounded-full font-medium">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {agent.capabilities && (
            <div className="flex flex-wrap gap-5 mt-5 pt-5 border-t border-gray-100">
              {Object.entries(agent.capabilities).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${val ? 'bg-primary' : 'bg-gray-300'}`} />
                  <span className={`text-xs font-medium capitalize ${val ? 'text-primary' : 'text-gray-400'}`}>
                    {key.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Underline tabs */}
        <div className="flex gap-6 border-b border-gray-200 mb-8">
          {[{ id: 'overview', label: 'Overview' }, { id: 'try', label: 'Try it' }].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {agent.skills?.length > 0 ? (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                  {agent.skills.length} {agent.skills.length === 1 ? 'Skill' : 'Skills'}
                </p>
                <div className="divide-y divide-gray-100">
                  {agent.skills.map(skill => (
                    <div key={skill.id} className="py-5 flex items-start gap-4">
                      <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Zap className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{skill.name}</h3>
                        {skill.description && (
                          <p className="text-sm text-gray-500 mt-0.5">{skill.description}</p>
                        )}
                        {skill.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2.5">
                            {skill.tags.map(t => (
                              <span key={t} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/20">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {skill.examples?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                              Try an example
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {skill.examples.map((ex, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    const firstReq = required[0];
                                    if (firstReq) setFormData(prev => ({ ...prev, [firstReq]: ex }));
                                    setActiveTab('try');
                                  }}
                                  className="text-xs text-gray-600 bg-white border border-gray-200 hover:border-primary hover:text-primary rounded-full px-3 py-1.5 transition-colors"
                                >
                                  {ex}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400">No skills available for this agent.</p>
            )}
          </div>
        )}

        {/* Try it Tab */}
        {activeTab === 'try' && (
          <div>
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-900">Test this agent</h2>
              <p className="text-sm text-gray-500 mt-0.5">Fill in the parameters and run the agent directly.</p>
            </div>

            {agent.skills?.length > 1 && (
              <div className="mb-6">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Skill</label>
                <select
                  value={selectedSkillIndex}
                  onChange={e => {
                    const idx = Number(e.target.value);
                    setSelectedSkillIndex(idx);
                    const initial = {};
                    Object.keys(agent.skills[idx]?.args_schema?.properties || {}).forEach(k => { initial[k] = ''; });
                    setFormData(initial);
                    setResponse(null);
                  }}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                >
                  {agent.skills.map((s, i) => (
                    <option key={s.id} value={i}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-5">
              {Object.entries(selectedSkill?.args_schema?.properties || {}).map(([key, schema]) => (
                <div key={key}>
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                    {key}
                    {required.includes(key) && <span className="text-red-400 ml-1 normal-case not-italic">*</span>}
                  </label>
                  {schema.description && (
                    <p className="text-xs text-gray-400 mb-1.5">{schema.description}</p>
                  )}
                  <input
                    type="text"
                    name={key}
                    value={formData[key] || ''}
                    onChange={handleInputChange}
                    placeholder={schema.description || key}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              ))}
            </div>

            <button
              onClick={handleTry}
              disabled={tryLoading}
              className="mt-4 px-2 py-1 bg-primary hover:bg-primaryDark disabled:opacity-50 text-white text-sm font-semibold rounded transition-all duration-200"
            >
              {tryLoading ? 'Running…' : 'Run agent'}
            </button>

            {error && (
              <div className="mt-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3">
                {error}
              </div>
            )}

            {response && (
              <div className="mt-6">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Response</p>
                <pre className="bg-gray-900 text-green-400 rounded-xl p-5 text-xs overflow-auto max-h-96 whitespace-pre-wrap leading-relaxed">
                  {JSON.stringify(response, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AgentDetailPage;
